import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, BotIcon, UserIcon, ArrowLeftIcon } from 'lucide-react';
interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}
interface AIChatModalProps {
  isOpen: boolean;
  onClose: () => void;
}
export function AIChatModal({
  isOpen,
  onClose
}: AIChatModalProps) {
  const [messages, setMessages] = useState<Message[]>([{
    role: 'assistant',
    content: "Hello! I'm your AI Pharmacist Assistant for Saudi Arabia. I can help with medication info, health questions, and wellness advice. How can I help you today?",
    timestamp: new Date()
  }]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  };
  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  useEffect(() => {
    if (isOpen) {
      // Lock body scroll on mobile when full-screen
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isOpen]);
  // Auto-grow textarea
  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 120)}px`;
    }
  }, [inputMessage]);
  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading) return;
    const userMessage: Message = {
      role: 'user',
      content: inputMessage,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);
    setError('');
    try {
      const response = await fetch('https://requests.magicpatterns.com/openai/proxy', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          model: 'gpt-4-turbo',
          messages: [{
            role: 'system',
            content: `You are an AI Pharmacist Assistant for Dawaak in Saudi Arabia.
RESPONSE FORMAT:
- Use clear sections with headers when appropriate
- Use bullet points (•) for lists
- Use line breaks for readability
- Bold important information using **text**
- ALWAYS include product links using this format: [Product Name](URL)
- Keep responses concise but informative (3-5 sentences or organized bullets)

YOUR ROLE:
• Answer medication and health questions
• Recommend products from Dawaak's catalog WITH LINKS
• Provide dosage and usage guidance
• Offer health and wellness advice

GUIDELINES:
✓ Always mention prices in SAR
✓ For serious symptoms → recommend seeing a doctor immediately
✓ Remind users you're an AI assistant, not a replacement for professional medical advice
✓ Serve Saudi customers with culturally sensitive, practical advice`
          }, ...messages.map(msg => ({
            role: msg.role,
            content: msg.content
          })), {
            role: 'user',
            content: inputMessage
          }],
          temperature: 0.7,
          max_tokens: 400
        })
      });
      if (!response.ok) {
        throw new Error('Failed to get response from AI');
      }
      const data = await response.json();
      const assistantMessage: Message = {
        role: 'assistant',
        content: data.choices[0].message.content,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (err) {
      setError('Sorry, I encountered an error. Please try again or contact Dawaak support.');
      console.error('AI Chat Error:', err);
    } finally {
      setIsLoading(false);
    }
  };
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };
  if (!isOpen) return null;
  const suggestedPrompts = ['Best vitamin for tiredness?', 'Headache remedies', 'Vitamin D dosage'];
  return <>
      {/* Desktop overlay */}
      <div className="hidden md:block fixed inset-0 bg-black bg-opacity-50 z-[200]" onClick={onClose} data-id="element-3027" data-mp="2jsgzefaadi1q" />
      {/* Container — full-screen on mobile, floating card on desktop */}
      <div className="
          fixed z-[201] bg-white flex flex-col
          inset-0
          md:inset-auto md:bottom-4 md:left-4 md:lg:left-6
          md:w-[400px] md:max-w-[calc(100vw-2rem)]
          md:max-h-[640px] md:rounded-2xl md:shadow-2xl
        " data-id="element-3028" data-mp="2sksp1yxkt1gn">
        {/* Header — sticky */}
        <div className="flex flex-shrink-0 items-center justify-between bg-brand px-3 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] md:rounded-t-2xl md:px-4" data-id="element-3029" data-mp="1w1lapr14n2yp">
          <div className="flex items-center gap-2 md:gap-3 min-w-0" data-id="element-3030" data-mp="1qpefpz9wnvqb">
            <button onClick={onClose} className="md:hidden text-white p-1.5 -ml-1.5 rounded-full active:bg-white/20" aria-label="Back" data-id="element-3031" data-mp="1qqpjhz3cp3hf">
              <ArrowLeftIcon className="w-5 h-5" data-id="element-3032" data-mp="1gwqmfei4w9wc" />
            </button>
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white md:h-10 md:w-10" data-id="element-3033" data-mp="24p743nxylzv4">
              <BotIcon className="h-5 w-5 text-brand md:h-6 md:w-6" data-id="element-3034" data-mp="29lo1z9uynsle" />
            </div>
            <div className="min-w-0" data-id="element-3035" data-mp="1uq88p727g0q2">
              <h3 className="text-white font-bold text-sm md:text-base truncate" data-id="element-3036" data-mp="1zpb21u8toii7">
                AI Pharmacist
              </h3>
              <p className="flex items-center gap-1 text-[11px] text-white/90 md:text-xs" data-id="element-3037" data-mp="ja9vcqzzusj1">
                <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-sage-light" data-id="element-3038" data-mp="obgz8ez0n7kf" />
                Online · Powered by AI
              </p>
            </div>
          </div>
          <button onClick={onClose} className="hidden md:block text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors" aria-label="Close" data-id="element-3039" data-mp="2lxgfzak6fc34">
            <XIcon className="w-5 h-5" data-id="element-3040" data-mp="2c8yj34f0pp4r" />
          </button>
        </div>
        {/* Messages */}
        <div className="flex-1 overflow-y-auto px-3 md:px-4 py-3 md:py-4 space-y-3 md:space-y-4 bg-gray-50 overscroll-contain" data-id="element-3041" data-mp="279c6mx70kcx0">
          {messages.map((message, index) => <div key={index} className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-3042" data-mp="1y85negczco02" data-mpi={index}>
              <div className="flex items-start gap-2 max-w-[85%]" data-id="element-3043" data-mp="1tavp0b7m5203">
                {message.role === 'assistant' && <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sage-light md:h-8 md:w-8" data-id="element-3044" data-mp="3skc9493i2n6m">
                    <BotIcon className="h-4 w-4 text-sage-dark md:h-5 md:w-5" data-id="element-3045" data-mp="3v7ee4dz9difu" />
                  </div>}
                <div className={`rounded-2xl px-3.5 py-2.5 md:px-4 md:py-3 ${message.role === 'user' ? 'bg-[#1E2B55] text-white rounded-br-md' : 'bg-white text-gray-800 border border-gray-200 shadow-sm rounded-bl-md'}`} data-id="element-3046" data-mp="3zibw9dfi0ho">
                  <div className="text-sm leading-relaxed" style={{
                wordBreak: 'break-word',
                direction: /[\u0600-\u06FF]/.test(message.content) ? 'rtl' : 'ltr'
              }} dangerouslySetInnerHTML={{
                __html: formatMessage(message.content)
              }} data-id="element-3047" data-mp="3c80f3hmo9hih" />
                </div>
                {message.role === 'user' && <div className="w-7 h-7 md:w-8 md:h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0 mt-1" data-id="element-3048" data-mp="16dzc9fxj3n48">
                    <UserIcon className="w-4 h-4 md:w-5 md:h-5 text-blue-600" data-id="element-3049" data-mp="1rjco62xuog90" />
                  </div>}
              </div>
            </div>)}
          {isLoading && <div className="flex justify-start" data-id="element-3050" data-mp="21a7a1676pdtx">
              <div className="flex items-start gap-2 max-w-[85%]" data-id="element-3051" data-mp="1whnu42l0y6ly">
                <div className="mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-sage-light md:h-8 md:w-8" data-id="element-3052" data-mp="1wgcpk0jr8uts">
                  <BotIcon className="h-4 w-4 text-sage-dark md:h-5 md:w-5" data-id="element-3053" data-mp="268qwd7powrmt" />
                </div>
                <div className="bg-white text-gray-800 border border-gray-200 shadow-sm rounded-2xl rounded-bl-md px-4 py-3" data-id="element-3054" data-mp="2k80b02y8zdoo">
                  <div className="flex gap-1" data-id="element-3055" data-mp="2f96wq8b4zing">
                    <div className="h-2 w-2 animate-bounce rounded-full bg-sage" style={{
                  animationDelay: '0ms'
                }} data-id="element-3056" data-mp="ptpj1lmcw3eo" />
                    <div className="h-2 w-2 animate-bounce rounded-full bg-sage" style={{
                  animationDelay: '150ms'
                }} data-id="element-3057" data-mp="2whq1prdp3i5d" />
                    <div className="h-2 w-2 animate-bounce rounded-full bg-sage" style={{
                  animationDelay: '300ms'
                }} data-id="element-3058" data-mp="2mrn5liba1o2t" />
                  </div>
                </div>
              </div>
            </div>}
          {error && <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800" data-id="element-3059" data-mp="23tke8wv9bf1u">
              {error}
            </div>}
          <div ref={messagesEndRef} data-id="element-3060" data-mp="28sx9a4wa7sfi" />
        </div>
        {/* Suggested prompts — only show when conversation is fresh */}
        {messages.length <= 1 && !isLoading && <div className="px-3 md:px-4 pb-2 flex gap-2 overflow-x-auto scrollbar-hide flex-shrink-0 bg-gray-50" data-id="element-3061" data-mp="6zzcjxzdwfpa">
            {suggestedPrompts.map(prompt => <button key={prompt} onClick={() => setInputMessage(prompt)} className="flex-shrink-0 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs text-gray-700 transition-colors duration-150 hover:border-sage" data-id="element-3062" data-mp="llcdehqlwf4f">
                {prompt}
              </button>)}
          </div>}
        {/* Composer — sticky bottom with safe area */}
        <div className="border-t border-gray-200 bg-white md:rounded-b-2xl flex-shrink-0 pb-[max(0.5rem,env(safe-area-inset-bottom))]" data-id="element-3063" data-mp="14j5fjhteol0r">
          <div className="flex items-end gap-2 p-3 md:p-4" data-id="element-3064" data-mp="pp0kz0f8napo">
            <textarea ref={inputRef} value={inputMessage} onChange={e => setInputMessage(e.target.value)} onKeyDown={handleKeyPress} placeholder="Ask about medications…" rows={1} className="max-h-[120px] flex-1 resize-none rounded-2xl border border-gray-300 px-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" disabled={isLoading} dir="auto" data-id="element-3065" data-mp="ukzgd7yzhszl" />
            <button onClick={sendMessage} disabled={isLoading || !inputMessage.trim()} className="flex-shrink-0 rounded-full bg-brand p-2.5 text-white transition-colors duration-150 hover:bg-brand-dark active:bg-brand-dark disabled:cursor-not-allowed disabled:opacity-50" aria-label="Send message" data-id="element-3066" data-mp="26e9vrl0qj3h6">
              <SendIcon className="w-5 h-5" data-id="element-3067" data-mp="1ng71aqpf4ebz" />
            </button>
          </div>
        </div>
      </div>
    </>;
}
function formatMessage(content: string): string {
  let formatted = content.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  formatted = formatted.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="inline-flex items-center gap-1 text-brand hover:text-brand-dark font-medium underline"><span>$1</span><svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10 6H6a2 2 0 00-2 2h10a2 2 0 002 2v-4M14 4h6m0 0v6m0-6L10 14"></path></svg></a>');
  formatted = formatted.replace(/^[•\-]\s+(.+)$/gm, '<div class="flex gap-2 my-1"><span class="text-sage-dark">•</span><span>$1</span></div>');
  formatted = formatted.replace(/\n/g, '<br>');
  formatted = formatted.replace(/\*\*([^:]+):\*\*/g, '<div class="font-bold text-gray-900 mt-3 mb-1">$1:</div>');
  return formatted;
}